import { z } from "zod";
import {
  isAllowedArtworkTemplate,
  isAllowedPreviewUrl,
  isAllowedProviderUrl,
} from "@/lib/music/links";
import type { MusicSong, ProfileMusicSong } from "@/lib/music/types";

/**
 * A song attached to something — a post, an event or experience banner, a
 * chat message. One shape everywhere, so one pill, one sheet and one card can
 * render any of them.
 *
 * Always a SERVER snapshot: clients send `MusicAttachmentInput` (provider +
 * catalog id) and `resolveMusicAttachment` reads every field from the catalog.
 * Stored copies are re-checked by `readMusicAttachment` before rendering, so a
 * row written by older code can't put an off-allowlist URL into an `src`.
 */
export interface MusicAttachment {
  v: 1;
  provider: "apple_music";
  songId: string;
  title: string;
  artistName: string;
  albumName: string | null;
  /** `{w}x{h}` template — resolve with `musicArtworkSrc`. */
  artworkUrl: string | null;
  artworkBgColor: string | null;
  artworkTextColor: string | null;
  previewUrl: string | null;
  providerUrl: string | null;
  isrc: string | null;
  explicit: boolean;
  durationMs: number | null;
}

/** Apple catalog ids are numeric strings. */
export const MusicCatalogIdSchema = z.string().regex(/^\d{1,20}$/);

/** What a client may send: which song, never what it looks like. */
export const MusicAttachmentInputSchema = z
  .object({
    provider: z.literal("apple_music"),
    songId: MusicCatalogIdSchema,
  })
  .strict();

export type MusicAttachmentInput = z.infer<typeof MusicAttachmentInputSchema>;

const nullableText = z.string().min(1).max(500).nullable().catch(null);

const StoredMusicAttachmentSchema = z.object({
  v: z.literal(1),
  provider: z.literal("apple_music"),
  songId: MusicCatalogIdSchema,
  title: z.string().min(1).max(500),
  artistName: z.string().min(1).max(500),
  albumName: nullableText,
  artworkUrl: nullableText,
  artworkBgColor: z
    .string()
    .regex(/^[0-9a-f]{6}$/i)
    .nullable()
    .catch(null),
  artworkTextColor: z
    .string()
    .regex(/^[0-9a-f]{6}$/i)
    .nullable()
    .catch(null),
  previewUrl: nullableText,
  providerUrl: nullableText,
  isrc: nullableText,
  explicit: z.boolean().catch(false),
  durationMs: z.number().int().positive().nullable().catch(null),
});

/**
 * Parse a stored attachment for rendering. Returns null for anything that
 * isn't one (an old row, a malformed blob) — music is optional, so a bad
 * value hides the pill instead of breaking the post.
 */
export function readMusicAttachment(raw: unknown): MusicAttachment | null {
  if (!raw || typeof raw !== "object") return null;
  const parsed = StoredMusicAttachmentSchema.safeParse(raw);
  if (!parsed.success) return null;
  const a = parsed.data;
  return {
    ...a,
    artworkUrl: isAllowedArtworkTemplate(a.artworkUrl) ? a.artworkUrl : null,
    previewUrl: isAllowedPreviewUrl(a.previewUrl) ? a.previewUrl : null,
    providerUrl: isAllowedProviderUrl(a.providerUrl) ? a.providerUrl : null,
  };
}

export function musicAttachmentFromSong(song: MusicSong): MusicAttachment {
  return {
    v: 1,
    provider: song.provider,
    songId: song.id,
    title: song.title,
    artistName: song.artistName,
    albumName: song.albumName,
    artworkUrl: song.artworkUrl,
    artworkBgColor: song.artworkBgColor,
    artworkTextColor: song.artworkTextColor,
    previewUrl: song.previewUrl,
    providerUrl: song.providerUrl,
    isrc: song.isrc,
    explicit: song.explicit,
    durationMs: song.durationMs,
  };
}

export function musicAttachmentFromProfileSong(
  song: ProfileMusicSong,
): MusicAttachment {
  return {
    v: 1,
    provider: song.provider,
    songId: song.providerSongId,
    title: song.title,
    artistName: song.artistName,
    albumName: song.albumName,
    artworkUrl: song.artworkUrl,
    artworkBgColor: song.artworkBgColor,
    artworkTextColor: song.artworkTextColor,
    previewUrl: song.previewUrl,
    providerUrl: song.providerUrl,
    isrc: song.isrc,
    explicit: song.explicit,
    durationMs: null,
  };
}

export function musicAttachmentInput(
  attachment: Pick<MusicAttachment, "provider" | "songId">,
): MusicAttachmentInput {
  return { provider: attachment.provider, songId: attachment.songId };
}
